import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  XMarkIcon
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { SURFACE_MUTED } from "../../components/UserInfo";

export interface DeleteUserModalProps {
  isOpen: boolean;
  subjectDisplayName: string;
  subjectUserId: string;
  isDeleting: boolean;
  errorMessage?: string;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * A confirmation rather than CC6's reassignment form: CC9's soft delete skips the legacy cleanup,
 * so there is no assignee to choose.
 */
export const DeleteUserModal = ({
  isOpen,
  subjectDisplayName,
  subjectUserId,
  isDeleting,
  errorMessage,
  onClose,
  onConfirm
}: DeleteUserModalProps) => {
  // Blocked in flight, so the outcome cannot land on a user selected afterwards.
  const handleClose = () => {
    if (isDeleting) {
      return;
    }

    onClose();
  };

  return isOpen ? (
    <Modal
      show
      data-testid={"usermanager-delete-modal"}
      footer={{
        footerComponent: (
          <Box
            sx={{
              borderTop: `1px solid ${SURFACE_MUTED}`,
              columnGap: "8px",
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 24px",
              width: "100%"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "usermanager-delete-cancel",
                eventProperties: undefined
              }}
              buttonStyle={"tertiary"}
              data-testid={"usermanager-delete-cancel"}
              disabled={isDeleting}
              onClick={handleClose}
            >
              {Locale.getText("CANCEL") as string}
            </Button>
            <Button
              analyticsConfiguration={{
                eventName: "usermanager-delete-confirm",
                eventProperties: undefined
              }}
              buttonStyle={"primary"}
              data-testid={"usermanager-delete-confirm"}
              disabled={isDeleting}
              onClick={onConfirm}
            >
              {
                Locale.getText(
                  isDeleting ? "DELETING" : "DELETE_USER"
                ) as string
              }
            </Button>
          </Box>
        )
      }}
      header={
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <Typography tag={"h2"} variant={"h5"}>
            {Locale.getText("DELETE_USER") as string}
          </Typography>
          <IconButton
            aria-label={Locale.getText("CLOSE") as string}
            buttonStyle={"tertiary"}
            data-testid={"usermanager-delete-dismiss"}
            disabled={isDeleting}
            size={"sm"}
            onClick={handleClose}
          >
            <XMarkIcon title={Locale.getText("CLOSE") as string} />
          </IconButton>
        </Box>
      }
      size={"small"}
      sx={{ "& .MuiDialog-paper": { maxWidth: "460px", width: "100%" } }}
      onHide={handleClose}
    >
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
        <Typography
          data-testid={"usermanager-delete-body"}
          tag={"p"}
          variant={"body-md"}
        >
          {
            Locale.getText("UM_DEL_BODY", {
              name: subjectDisplayName,
              userId: subjectUserId
            }) as string
          }
        </Typography>

        {errorMessage ? (
          <Alert
            data-testid={"usermanager-delete-error"}
            role={"alert"}
            type={"error"}
          >
            {errorMessage}
          </Alert>
        ) : null}
      </Box>
    </Modal>
  ) : null;
};
